import type { recommendedVoice } from "@/helpers/types";

export function useReadAloudVoices() {
  return { getRecommendedVoices, getSpeechSynthesisVoices };
}

async function getRecommendedVoices(): Promise<recommendedVoice[]> {
  const languageLists = await getLanguageRecommendedList();

  return languageLists.flatMap((voice) => {
    const altVoices =
      voice.altNames?.map((altName) => ({
        ...voice,
        name: altName,
      })) ?? [];
    return [voice, ...altVoices];
  });
}

// Get recommended list of voices filtered by language
async function getLanguageRecommendedList(): Promise<recommendedVoice[]> {
  return (await import("@/locales/recommendedVoices")).default;
}

/**
 * Get the voices from web speech API, this function will wait until the voices are loaded
 * If the voices are not loaded after the timeout, it will reject the promise (fallback for browsers that don't support the voiceschanged event)
 * @param timeout
 * @returns
 */
function getSpeechSynthesisVoices(
  timeout = 5000
): Promise<SpeechSynthesisVoice[]> {
  return new Promise((resolve) => {
    // If the browser doesn't support speechSynthesis API, we return an empty voices array
    if (!window.speechSynthesis) {
      resolve([]);
      return;
    }

    let voices = window.speechSynthesis?.getVoices();

    // Fallback to review every 100ms (sometimes event voiceschanged is not supported in mobile browsers)
    const checkVoices = setInterval(() => {
      voices = window.speechSynthesis?.getVoices();
      if (voices.length > 0) {
        clearInterval(checkVoices);
        clearTimeout(timeoutId);
        resolve(voices);
        return;
      }
    }, 100);

    // Time limit to avoid an infinite loop
    const timeoutId = setTimeout(() => {
      clearInterval(checkVoices);
      resolve([]);
    }, timeout);

    // Listen to the event for voices load (if the browser supports it)
    window.speechSynthesis?.addEventListener("voiceschanged", () => {
      voices = window.speechSynthesis?.getVoices();
      clearInterval(checkVoices);
      clearTimeout(timeoutId);
      resolve(voices);
    });
  });
}
